import { TouchEvent, useRef } from 'react';

interface UseSwipeOptions {
  onSwipeLeft: () => void; // swipe towards the left → next
  onSwipeRight: () => void; // swipe towards the right → previous
  millisecondThreshold?: number; // min touch length in ms to trigger a swipe
  horizontalRestraint?: number; // min pixels on the X axis to trigger a swipe
}

interface SwipeHandlers {
  onTouchStart: (event: TouchEvent) => void;
  onTouchMove: (event: TouchEvent) => void;
  onTouchEnd: () => void;
}

// Shared horizontal swipe detection for carousels/sliders.
export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  millisecondThreshold = 50,
  horizontalRestraint = 50,
}: UseSwipeOptions): SwipeHandlers {
  const startX = useRef(0);
  const startY = useRef(0);
  const distanceX = useRef(0);
  const distanceY = useRef(0);
  const startTime = useRef(0);

  const onTouchStart = (event: TouchEvent): void => {
    const { pageX, pageY } = event.changedTouches[0];
    startTime.current = new Date().getTime();
    startX.current = pageX;
    startY.current = pageY;
  };

  const onTouchMove = (event: TouchEvent): void => {
    const { pageX, pageY } = event.changedTouches[0];
    distanceX.current = pageX - startX.current;
    distanceY.current = pageY - startY.current;
  };

  const onTouchEnd = (): void => {
    const ellapsedTime = new Date().getTime() - startTime.current;
    if (
      ellapsedTime > millisecondThreshold &&
      Math.abs(distanceX.current) >= horizontalRestraint &&
      Math.abs(distanceX.current) > Math.abs(distanceY.current)
    ) {
      if (distanceX.current < 0) {
        onSwipeLeft();
      } else {
        onSwipeRight();
      }
    }
    distanceX.current = 0;
    distanceY.current = 0;
  };

  return { onTouchStart, onTouchMove, onTouchEnd };
}

